import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Table from '../../../core/form/Table';
import { browserHistory } from 'react-router';
import { startCase, upperFirst } from 'lodash';
import Button from '../../../core/form/Button';
import Async from '../../../core/layout/Async';
import Dropdown from '../../../core/layout/Dropdown';
import PostPaymentContractProductsList from './PostPaymentContractProductsList';
import UpfrontContractProductsList from './UpfrontContractProductsList';

const ContractList = (props) => {
  return (
    <Async fetching={props.fetching}>
      <Table
        expandableComponent={(row) => {
          if (row.version !== 'v2') {
            return <PostPaymentContractProductsList data={row.products} loading={props.fetching} readOnly />;
          }
          if (!!row.products?.length) {
            if (row.type === 'UPFRONT') {
              return <UpfrontContractProductsList data={row.products} loading={props.fetching} readOnly />;
            }
            if (row.type === 'POST_PAYMENT') {
              return <PostPaymentContractProductsList data={row.products} loading={props.fetching} readOnly />;
            }
          }
        }}
        columns={[
          {
            label: 'Document Number',
            path: 'documentNumber',
            width: '150px',
            render: (value, row) => {
              if (row.version === 'v2') {
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      height: '32px',
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#411596',
                        color: 'white',
                        borderRadius: '50%',
                        height: '18px',
                        width: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '10px',
                      }}
                    >
                      {row.version}
                    </span>
                    {value}
                  </div>
                );
              }

              return value;
            },
          },
          {
            label: 'Social Reason',
            path: 'socialReason',
          },
          {
            label: 'Company',
            path: 'company.name',
            width: '150px',
          },
          {
            label: 'Segment',
            path: 'company.segment.name',
            width: '120px',
          },
          {
            label: 'Distributor',
            path: 'distributor.name',
            width: '150px',
          },
          {
            label: 'Contract Start',
            path: 'startsAt',
            width: '100px',
            render: (value) => moment(value).utc().format('DD/MM/YYYY'),
          },
          {
            label: 'Contract End',
            path: 'endsAt',
            width: '100px',
            render: (value) => (value ? moment(value).utc().format('DD/MM/YYYY') : null),
          },
          {
            label: 'Status',
            path: 'status',
            width: ['ACTIVE', 'CANCELED', 'BLOCKED'].includes(props.status) ? '100px' : '300px',
            render: (value) => upperFirst(startCase(value.toLowerCase())),
          },
          ...(props.contracts.some((contract) => contract.status === 'CANCELED')
            ? [
                {
                  label: 'Canceled At',
                  path: 'canceledAt',
                  width: '100px',
                  render: (value) => (value ? moment(value).utc().format('DD/MM/YYYY') : null),
                },
              ]
            : []),
          {
            label: 'Type',
            path: 'type',
            width: '100px',
            render: (value) => (value ? upperFirst(startCase(value.toLowerCase())) : '-'),
          },
          ...(!props.contracts.some((contract) => contract.status !== 'BLOCKED')
            ? [
                {
                  label: 'Blocked Date',
                  path: 'blockedAt',
                  width: ['ACTIVE', 'CANCELED', 'BLOCKED'].includes(props.status) ? '100px' : '180px',
                  render: (value) => (value ? moment(value).utc().format('DD/MM/YYYY') : null),
                },
              ]
            : []),
          {
            label: 'Actions',
            width: ['ACTIVE'].includes(props.status) ? '170px' : '120px',
            render: (cell, row) => {
              const menuItems = [
                ...(row.status === 'ACTIVE'
                  ? [
                      {
                        key: row.id,
                        onClick: () =>
                          browserHistory.push({
                            pathname: `/contracts/${row.id}`,
                            state: { from: { text: 'Contracts', pathname: '/contracts' } },
                          }),
                        icon: 'edit',
                        label: 'Edit',
                      },
                      {
                        key: row.id,
                        onClick: () => props.onAction('upgrade', row),
                        icon: 'upload',
                        label: 'Upgrade',
                      },
                      ...(row.status === 'ACTIVE'
                        ? [
                            {
                              key: row.id,
                              onClick: () => props.onAction('downgrade', row),
                              icon: 'download',
                              label: 'Downgrade',
                            },
                            {
                              key: row.id,
                              onClick: () => props.onAction('cancel', row),
                              icon: 'close-circle',
                              label: 'Cancel',
                            },
                            ...(!!row.paymentGatewayCreditCardMethod
                              ? [
                                  {
                                    key: row.id,
                                    onClick: () => props.onAction('remove-credit-card', row),
                                    icon: 'credit-card',
                                    label: 'Remove Credit Card',
                                  },
                                ]
                              : []),
                            {
                              key: row.id,
                              onClick: () => props.onAction('block', row),
                              icon: 'lock',
                              label: 'Block',
                            },
                          ]
                        : []),
                    ]
                  : []),
              ];

              return (
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Button
                    icon="eye"
                    tooltip="Details"
                    onClick={() =>
                      browserHistory.push({
                        pathname: `/contracts/${row.id}/detail`,
                        state: { from: { text: 'Contracts', pathname: '/contracts' } },
                      })
                    }
                  />
                  {[
                    'ACTIVE',
                    'CANCELED',
                    'BLOCKED',
                    'AWAITING_INITIAL_PAYMENT',
                    'AWAITING_UPGRADE_PAYMENT',
                    'AWAITING_REACTIVATE_PAYMENT',
                  ].includes(props.status) && (
                    <Button
                      icon="shopping-cart"
                      tooltip="View bills"
                      onClick={() => {
                        browserHistory.push({
                          state: { from: { text: 'Contracts', pathname: '/billing' } },
                          pathname: `/billing`,
                          search: `?documentNumber=${row.documentNumber}`,
                        });
                      }}
                    />
                  )}
                  {props.status === 'CANCELED' && (
                    <>
                      <Button
                        icon="check"
                        tooltip="Reactivate"
                        onClick={() => {
                          browserHistory.push({
                            state: { from: { text: 'Contracts', pathname: '/reactivate' } },
                            pathname: `/contracts/${row.id}/reactivate`,
                          });
                        }}
                      />
                      <Button
                        icon="reload"
                        tooltip="Force contract reactivation without creating an invoice"
                        onClick={() => {
                          props.onAction('force-reactivation', row);
                        }}
                      />
                    </>
                  )}
                  {props.status === 'AWAITING_ANNUAL_RENOVATION' && (
                    <Button
                      icon="check"
                      tooltip="Confirm renewal"
                      onClick={() => {
                        browserHistory.push({
                          state: { from: { text: 'Contracts', pathname: '/confirm-renewal' } },
                          pathname: `/contracts/${row.id}/confirm-renewal`,
                        });
                      }}
                    />
                  )}
                  {row.status === 'ACTIVE' && <Dropdown menuItems={menuItems} type="click" buttonPlace="bottomRight" />}
                  {row.status === 'BLOCKED' && (
                    <>
                      <Button
                        icon="edit"
                        tooltip="Edit Contract"
                        onClick={() =>
                          browserHistory.push({
                            pathname: `/contracts/${row.id}`,
                            state: { from: { text: 'Contracts', pathname: '/contracts' } },
                          })
                        }
                      />
                      <Button
                        icon="unlock"
                        tooltip="Unblock Contract"
                        onClick={() => {
                          props.onAction('unblock', row);
                        }}
                      />
                    </>
                  )}
                  {[
                    'AWAITING_INITIAL_START_DATE',
                    'AWAITING_UPGRADE_START_DATE',
                    'AWAITING_RENOVATE_START_DATE',
                    'AWAITING_DOWNGRADE_START_DATE',
                    'AWAITING_INITIAL_PAYMENT',
                    'AWAITING_UPGRADE_PAYMENT',
                    'AWAITING_REACTIVATE_PAYMENT',
                    'AWAITING_ANNUAL_RENOVATION',
                  ].includes(props.status) && (
                    <Button
                      icon="close-circle"
                      tooltip="Delete Contract"
                      onClick={() => props.onAction('delete', row)}
                    />
                  )}
                  {['AWAITING_UPGRADE_START_DATE', 'AWAITING_DOWNGRADE_START_DATE'].includes(props.status) &&
                    row.version === 'v2' && (
                      <Button
                        icon="carry-out"
                        tooltip={
                          {
                            AWAITING_UPGRADE_START_DATE: 'Antecipate upgrade',
                            AWAITING_DOWNGRADE_START_DATE: 'Antecipate downgrade',
                          }[props.status]
                        }
                        onClick={() => {
                          props.onAction('anticipate-activation', row);
                        }}
                      />
                    )}
                </div>
              );
            },
          },
        ]}
        rows={props.contracts}
        pagination={props.pagination}
        onChange={props.onChange}
      />
    </Async>
  );
};

ContractList.propTypes = {
  contracts: PropTypes.array.isRequired,
  pagination: PropTypes.shape({
    total: PropTypes.number,
    current: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
  fetching: PropTypes.bool.isRequired,
};

export default ContractList;
